import type { GalleryPhoto } from 'components/marketplace/helpers/galleryPhotos';
import type {
  PresetPictureSet,
  PresetPictureSetOptions,
} from 'components/shared/performance/pictures';
import { generatePresetPictureSetProps, Picture } from 'components/shared/performance/pictures';
import { isCabinetPresizedUrl, toImagesWorkerSourceUrl, withImageParams } from 'helpers/imageUrl';

export type GalleryPhotoTier = 'md' | 'lg' | 'xl';

export type GalleryBreakpointTier = PresetPictureSet[number] & {
  tier: GalleryPhotoTier;
};

export const PDP_MOBILE_CAROUSEL_BREAKPOINT_TIERS: GalleryBreakpointTier[] = [
  { minWidth: 0, maxWidth: 385, preset: 'square-512', presetScale: '3/4', tier: 'md' },
  { minWidth: 385, maxWidth: 513, preset: 'square-512', tier: 'md' },
  { minWidth: 513, maxWidth: 768, preset: 'square-1024', presetScale: '3/4', tier: 'md' },
];

export const PDP_MOBILE_MODAL_BREAKPOINT_TIERS: GalleryBreakpointTier[] = [
  { minWidth: 0, maxWidth: 376, preset: '3:2-xs', tier: 'md' },
  { minWidth: 376, maxWidth: 513, preset: '3:2-sm', presetScale: '2/3', tier: 'md' },
  { minWidth: 513, maxWidth: 768, preset: '3:2-sm', tier: 'md' },
];

export const GALLERY_BREAKPOINT_TIERS: GalleryBreakpointTier[] = [
  { minWidth: 768, maxWidth: 1025, preset: '3:2-sm', presetScale: '2/3', tier: 'md' },
  { minWidth: 1025, maxWidth: 1505, preset: '3:2-sm', tier: 'lg' },
  { minWidth: 1505, maxWidth: 1817, preset: '3:2-lg', presetScale: '3/4', tier: 'xl' },
  { minWidth: 1817, maxWidth: 2087, preset: '3:2-xl', presetScale: '3/4', tier: 'xl' },
  { minWidth: 2087, maxWidth: null, preset: '3:2-xl', presetScale: '1', tier: 'xl' },
];

export const GALLERY_SPLIT_BREAKPOINT_TIERS: GalleryBreakpointTier[] = [
  { minWidth: 768, maxWidth: 1490, preset: '3:2-xs', tier: 'md' },
  { minWidth: 1490, maxWidth: 1766, preset: '3:2-sm', presetScale: '2/3', tier: 'md' },
  { minWidth: 1766, maxWidth: 2280, preset: '3:2-sm', tier: 'md' },
  { minWidth: 2280, maxWidth: null, preset: '3:2-lg', presetScale: '3/4', tier: 'lg' },
];

export const MAP_CAROUSEL_BREAKPOINT_TIERS: GalleryBreakpointTier[] = [
  ...PDP_MOBILE_MODAL_BREAKPOINT_TIERS,
  { minWidth: 768, maxWidth: 1536, preset: '3:2-sm', tier: 'md' },
  { minWidth: 1536, maxWidth: 2160, preset: '3:2-lg', presetScale: '3/4', tier: 'lg' },
  { minWidth: 2160, maxWidth: 2700, preset: '3:2-xl', presetScale: '3/4', tier: 'xl' },
  { minWidth: 2700, maxWidth: null, preset: '3:2-xl', presetScale: '1', tier: 'xl' },
];

export const LIGHTBOX_BREAKPOINT_TIERS: GalleryBreakpointTier[] = [
  { minWidth: 0, maxWidth: 375, preset: '3:2-xs', tier: 'md' },
  { minWidth: 375, maxWidth: 512, preset: '3:2-sm', presetScale: '2/3', tier: 'md' },
  { minWidth: 512, maxWidth: 768, preset: '3:2-sm', tier: 'lg' },
  { minWidth: 768, maxWidth: 1280, preset: '3:2-lg', presetScale: '3/4', tier: 'xl' },
  { minWidth: 1280, maxWidth: 1920, preset: '3:2-xl', presetScale: '3/4', tier: 'xl' },
  { minWidth: 1920, maxWidth: null, preset: '3:2-xl', presetScale: '1', tier: 'xl' },
];

const PRESIZED_TIER_FALLBACK_ORDER: Record<GalleryPhotoTier, GalleryPhotoTier[]> = {
  md: ['md', 'lg', 'xl'],
  lg: ['lg', 'xl', 'md'],
  xl: ['xl', 'lg', 'md'],
};

const LARGEST_PRESIZED_TIERS: GalleryPhotoTier[] = PRESIZED_TIER_FALLBACK_ORDER.xl;

export const pickPresizedUrlForGalleryTier = (
  photo: GalleryPhoto,
  tier: GalleryPhotoTier,
): string | undefined => pickFirstPresizedUrl(photo, PRESIZED_TIER_FALLBACK_ORDER[tier]);

const getPhotoUrlForTier = (
  photo: GalleryPhoto,
  tier: GalleryPhotoTier,
): string | null | undefined => {
  if (tier === 'xl') {
    return photo.urlXl;
  }
  if (tier === 'lg') {
    return photo.urlLg;
  }
  return photo.url;
};

export const buildWorkerPhotoUrl = (
  baseUrl: string,
  options: {
    preset: string;
    presetScale?: string;
    dpr?: string;
    service?: string;
    fit?: 'cover' | 'contain';
  },
): string =>
  withImageParams(baseUrl, {
    preset: options.preset,
    ...(options.presetScale ? { presetScale: options.presetScale } : {}),
    service: options.service ?? 'cosmos',
    auto: 'format,compress',
    dpr: options.dpr ?? '1',
    ...(options.fit ? { fit: options.fit } : {}),
  });

export const pickFirstPresizedUrl = (
  photo: GalleryPhoto,
  tiers: ReadonlyArray<GalleryPhotoTier> = LARGEST_PRESIZED_TIERS,
): string | undefined => {
  if (photo.isFloorPlan) {
    return undefined;
  }
  for (const tier of tiers) {
    const candidate = getPhotoUrlForTier(photo, tier);
    if (candidate && isCabinetPresizedUrl(candidate)) {
      return candidate;
    }
  }
  return undefined;
};

export const getLightboxThumbnailUrl = (photo: GalleryPhoto): string => {
  const workerSource = toImagesWorkerSourceUrl(photo.url);
  if (workerSource) {
    return buildWorkerPhotoUrl(workerSource, {
      preset: '3:2-sm',
      presetScale: '1/8',
      service: 'lightbox',
      dpr: '2',
      ...(photo.isFloorPlan ? { fit: 'contain' } : {}),
    });
  }
  return pickFirstPresizedUrl(photo, ['md', 'lg', 'xl']) ?? photo.url;
};

const findResponsivePhotoSource = (
  photo: GalleryPhoto,
  fit?: 'cover' | 'contain',
): string | undefined => {
  const candidates =
    fit === 'contain'
      ? [photo.urlLg, photo.url, photo.urlXl]
      : [photo.urlXl, photo.urlLg, photo.url];
  for (const candidate of candidates) {
    if (!candidate) {
      continue;
    }
    const workerSource = toImagesWorkerSourceUrl(candidate);
    if (workerSource) {
      return workerSource;
    }
  }
  return undefined;
};

export const generateGalleryPictureSet = (
  props: Omit<PresetPictureSetOptions, 'photoUrl' | 'sizes'> & {
    photo: GalleryPhoto;
    sizes: GalleryBreakpointTier[];
  },
) => {
  const {
    photo,
    sizes,
    lazy,
    preload = false,
    rounded,
    objectFit = 'cover',
    fetchPriority,
    alt,
    dpr,
    quality,
    imageRef,
    fit,
  } = props;

  const responsivePhotoSource = findResponsivePhotoSource(photo, fit);
  const fullFramePresizedUrl =
    fit === 'contain' ? pickFirstPresizedUrl(photo, ['lg', 'md']) : undefined;
  const hasPresizedTier =
    fit === 'contain'
      ? fullFramePresizedUrl !== undefined
      : pickFirstPresizedUrl(photo) !== undefined && (!responsivePhotoSource || !fit);

  if (hasPresizedTier) {
    // oxlint-disable-next-line unicorn/no-array-sort -- Cosmos still targets browsers without Array.prototype.toSorted.
    const sortedSizes = [...sizes].sort((a, b) => a.minWidth - b.minWidth);
    const sources = sortedSizes.map(({ minWidth, maxWidth, tier }) => {
      const url = fullFramePresizedUrl ?? pickPresizedUrlForGalleryTier(photo, tier) ?? photo.url;
      const media =
        maxWidth === null
          ? `(min-width: ${minWidth}px)`
          : `(min-width: ${minWidth}px) and (max-width: ${maxWidth - 1}px)`;
      return { srcSet: url, media, key: `${photo.url}-${minWidth}`, fallbackSrc: url };
    });
    const smallestTier = sortedSizes[0]?.tier ?? 'md';
    const fallbackSrc =
      fullFramePresizedUrl ?? pickPresizedUrlForGalleryTier(photo, smallestTier) ?? photo.url;

    return (
      <Picture
        preload={preload}
        lazy={lazy}
        rounded={rounded}
        sources={sources}
        fallbackSrc={fallbackSrc}
        objectFit={objectFit}
        imageRef={imageRef}
        fetchPriority={fetchPriority}
        alt={alt}
      />
    );
  }

  return (
    <Picture
      {...generatePresetPictureSetProps({
        photoUrl: responsivePhotoSource ?? photo.url,
        sizes,
        lazy,
        preload,
        rounded,
        objectFit,
        fetchPriority,
        alt,
        dpr,
        quality,
        imageRef,
        fit: fit ?? (photo.isFloorPlan ? 'contain' : undefined),
      })}
    />
  );
};
